import { useState } from "react";
import Logo from "../components/Logo";
import { COLORS, FONTS } from "../constants/theme";
import { useAuth } from "../hooks/useAuth";

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
// Handles three views: login | signup | forgot
// Auth logic lives in useAuth() hook — this component is pure UI.

export default function LoginPage({ mode = "login", onSuccess, onBack }) {
  const [view, setView] = useState(mode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [sent, setSent] = useState(false);

  const { login, signup, loading, error, setError } = useAuth();

  const handleLogin = async () => {
    const { user } = await login({ email, password });
    if (user) onSuccess(user);
  };

  const handleSignup = async () => {
    if (!email || !password || !name || !company) {
      setError("Please fill in all fields.");
      return;
    }
    const { user } = await signup({ email, password, name, company });
    if (user) onSuccess(user);
  };

  const handleForgot = () => {
    if (!email) { setError("Enter your email first."); return; }
    setTimeout(() => setSent(true), 1000);
  };

  const switchView = (v) => { setView(v); setError(""); setSent(false); };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bgBase, display: "flex", fontFamily: FONTS.body }}>

      {/* ── LEFT PANEL — Form ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "60px 40px", position: "relative", overflow: "hidden" }}>
        {/* Grid bg */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#1a2035 1px,transparent 1px),linear-gradient(90deg,#1a2035 1px,transparent 1px)", backgroundSize: "50px 50px", opacity: .2 }} />
        <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translateX(-50%)", width: 500, height: 400, background: "radial-gradient(ellipse,#1d4ed820 0%,transparent 70%)" }} />

        <div style={{ position: "relative", width: "100%", maxWidth: 400 }}>
          <div style={{ marginBottom: 48 }}>
            <Logo size="md" />
          </div>

          {/* ── LOGIN VIEW ── */}
          {view === "login" && (
            <>
              <h1 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: 28, letterSpacing: "-.03em", marginBottom: 8, color: COLORS.textPrimary }}>Welcome back</h1>
              <p style={{ color: COLORS.textSecondary, fontSize: 14, marginBottom: 32 }}>Sign in to your RevCentriQ account</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Field label="EMAIL" type="email" placeholder="you@company.com" value={email} onChange={setEmail} onEnter={handleLogin} />
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <FieldLabel>PASSWORD</FieldLabel>
                    <button style={{ background: "none", border: "none", color: COLORS.iqBlueMid, fontSize: 12, cursor: "pointer" }} onClick={() => switchView("forgot")}>Forgot password?</button>
                  </div>
                  <input className="input-field" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLogin()} />
                </div>
                <ErrorMsg>{error}</ErrorMsg>
                <button className="btn-primary" style={{ width: "100%", marginTop: 4, opacity: loading ? .7 : 1 }} onClick={handleLogin} disabled={loading}>
                  {loading ? "Signing in…" : "Sign in"}
                </button>
              </div>
              <p style={{ textAlign: "center", marginTop: 28, fontSize: 13, color: COLORS.textSecondary }}>
                No account?{" "}
                <InlineLink onClick={() => switchView("signup")}>Create one free →</InlineLink>
              </p>
            </>
          )}

          {/* ── SIGNUP VIEW ── */}
          {view === "signup" && (
            <>
              <h1 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: 28, letterSpacing: "-.03em", marginBottom: 8, color: COLORS.textPrimary }}>Create your account</h1>
              <p style={{ color: COLORS.textSecondary, fontSize: 14, marginBottom: 32 }}>Start your 14-day free trial. No credit card needed.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Field label="FULL NAME" type="text" placeholder="Alex Rivera" value={name} onChange={setName} />
                <Field label="COMPANY" type="text" placeholder="Acme Corp" value={company} onChange={setCompany} />
                <Field label="WORK EMAIL" type="email" placeholder="alex@acmecorp.com" value={email} onChange={setEmail} />
                <Field label="PASSWORD" type="password" placeholder="8+ characters" value={password} onChange={setPassword} onEnter={handleSignup} />
                <ErrorMsg>{error}</ErrorMsg>
                <button className="btn-primary" style={{ width: "100%", marginTop: 4, opacity: loading ? .7 : 1 }} onClick={handleSignup} disabled={loading}>
                  {loading ? "Creating account…" : "Create account →"}
                </button>
              </div>
              <p style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: COLORS.textSecondary }}>
                Already have an account?{" "}
                <InlineLink onClick={() => switchView("login")}>Sign in</InlineLink>
              </p>
            </>
          )}

          {/* ── FORGOT VIEW ── */}
          {view === "forgot" && (
            <>
              <h1 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: 28, letterSpacing: "-.03em", marginBottom: 8, color: COLORS.textPrimary }}>Reset password</h1>
              <p style={{ color: COLORS.textSecondary, fontSize: 14, marginBottom: 32 }}>We'll send a reset link to your email.</p>
              {!sent ? (
                <>
                  <Field label="EMAIL" type="email" placeholder="you@company.com" value={email} onChange={setEmail} onEnter={handleForgot} />
                  <ErrorMsg>{error}</ErrorMsg>
                  <button className="btn-primary" style={{ width: "100%", marginTop: 14, opacity: loading ? .7 : 1 }} onClick={handleForgot} disabled={loading}>
                    {loading ? "Sending…" : "Send reset link"}
                  </button>
                </>
              ) : (
                <div style={{ background: "#05996918", border: "1px solid #05996944", borderRadius: 10, padding: "20px", textAlign: "center" }}>
                  <p style={{ color: "#34d399", fontWeight: 600, marginBottom: 8 }}>✓ Check your inbox</p>
                  <p style={{ color: COLORS.textSecondary, fontSize: 13 }}>We sent a reset link to {email}</p>
                </div>
              )}
              <p style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: COLORS.textSecondary }}>
                <InlineLink onClick={() => switchView("login")}>← Back to sign in</InlineLink>
              </p>
            </>
          )}
        </div>
      </div>

      {/* ── RIGHT PANEL — Brand ── */}
      <div style={{ flex: 1, background: "linear-gradient(160deg,#0a0e1a,#050810)", borderLeft: `1px solid ${COLORS.borderLight}`, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "60px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: "30%", left: "50%", transform: "translateX(-50%)", width: 500, height: 400, background: "radial-gradient(ellipse,#1d4ed820 0%,transparent 70%)" }} />
        <div style={{ position: "absolute", bottom: "10%", right: "5%", width: 280, height: 280, background: "radial-gradient(circle,#7c3aed10,transparent 70%)" }} />
        <div style={{ position: "relative", textAlign: "center" }}>
          <Logo size="lg" style={{ justifyContent: "center", marginBottom: 16 }} />
          <p style={{ color: COLORS.textMuted, fontSize: 13, fontFamily: FONTS.mono, letterSpacing: ".08em", textTransform: "uppercase", marginTop: 16 }}>
            GTM Intelligence Platform
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Small helper components (local to LoginPage) ───────────────────────────────

function FieldLabel({ children }) {
  return (
    <label style={{ fontSize: 12, color: "#8892b0", fontFamily: FONTS.mono, letterSpacing: ".05em", display: "block", marginBottom: 6 }}>
      {children}
    </label>
  );
}

function Field({ label, type, placeholder, value, onChange, onEnter }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <input
        className="input-field"
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
      />
    </div>
  );
}

function ErrorMsg({ children }) {
  if (!children) return null;
  return (
    <p style={{ color: COLORS.error, fontSize: 12, fontFamily: FONTS.mono, background: COLORS.errorBg, border: `1px solid ${COLORS.errorBorder}`, borderRadius: 8, padding: "10px 14px" }}>
      {children}
    </p>
  );
}

function InlineLink({ children, onClick }) {
  return (
    <button style={{ background: "none", border: "none", color: COLORS.iqBlueMid, cursor: "pointer", fontSize: 13 }} onClick={onClick}>
      {children}
    </button>
  );
}
